import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X, ChevronDown } from "lucide-react";

const navLinks = [
  { label: "Home", href: "/", hash: "#home" },
  { label: "Services", href: "/", hash: "#services" },
  { label: "How We Work", href: "/", hash: "#process" },
  { label: "Devops Tech Tools", href: "https://contract-alert-hub.emergent.host/devops-tools", external: true },
  { label: "Assessment", href: "/assessment" },
  { label: "Blog", href: "/", hash: "#insights" },
  { label: "About", href: "/about" },
];

const Logo = ({ scrolled }) => (
  <Link to="/" className="flex items-center gap-2">
    <div className="relative w-9 h-9">
      <span className="absolute top-0 left-1 w-5 h-5 rounded-full bg-[#2E6BF6] opacity-90" />
      <span className="absolute top-1 left-3 w-5 h-5 rounded-full bg-[#16C47F] opacity-90 mix-blend-multiply" />
      <span className="absolute top-3 left-0 w-5 h-5 rounded-full bg-[#F06A3E] opacity-90 mix-blend-multiply" />
    </div>
    <span className={`font-extrabold text-lg tracking-tight transition-colors ${
      scrolled ? 'text-[#0A1F47]' : 'text-white'
    }`}>NSTI</span>
  </Link>
);

export default function Navbar({ variant = "transparent" }) {
  // variant: transparent (home) or solid (subpages)
  const [scrolled, setScrolled] = useState(variant === "solid");
  const [open, setOpen] = useState(false);
  const [techDropdown, setTechDropdown] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (variant === "solid") {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  const renderLink = (l) => {
    // Handle external links
    if (l.external) {
      return (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-sm font-semibold transition-colors ${
            scrolled ? 'text-[#0A1F47]/80 hover:text-[#2E6BF6]' : 'text-white hover:text-white/80'
          }`}
        >
          {l.label}
        </a>
      );
    }

    // Handle Tech dropdown (no longer used, but keeping for reference)
    if (l.dropdown) {
      return (
        <div
          key={l.label}
          className="relative"
          onMouseEnter={() => setTechDropdown(true)}
          onMouseLeave={() => setTechDropdown(false)}
        >
          <button className={`text-sm font-semibold transition-colors inline-flex items-center gap-1 ${
            scrolled ? 'text-[#0A1F47]/80 hover:text-[#2E6BF6]' : 'text-white hover:text-white/80'
          }`}>
            {l.label}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {techDropdown && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#E2E8F4] py-2 z-50">
              {l.dropdown.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="block px-4 py-2 text-sm font-medium text-[#0A1F47]/80 hover:text-[#2E6BF6] hover:bg-[#F5F7FA] transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    const target = isHome && l.hash ? l.hash : `${l.href}${l.hash || ""}`;
    const isAnchor = isHome && l.hash;
    const linkClass = `text-sm font-semibold transition-colors ${
      scrolled ? 'text-[#0A1F47]/80 hover:text-[#2E6BF6]' : 'text-white hover:text-white/80'
    }`;
    
    if (isAnchor) {
      return (
        <a
          key={l.label}
          href={l.hash}
          onClick={() => setOpen(false)}
          className={linkClass}
        >
          {l.label}
        </a>
      );
    }
    return (
      <Link
        key={l.label}
        to={target}
        onClick={() => setOpen(false)}
        className={linkClass}
      >
        {l.label}
      </Link>
    );
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-[#E2E8F4]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Logo scrolled={scrolled} />
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map(renderLink)}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to={isHome ? "#contact" : "/#contact"}
            onClick={() => {
              if (isHome) {
                setTimeout(() => {
                  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                }, 50);
              }
            }}
            className="btn-navy hidden sm:inline-flex"
          >
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-md border border-[#0A1F47]/20"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E2E8F4]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((l) => {
              // Handle external links in mobile menu
              if (l.external) {
                return (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold text-[#0A1F47]/80 hover:text-[#2E6BF6] transition-colors"
                  >
                    {l.label}
                  </a>
                );
              }
              // Handle dropdown (legacy - no longer used)
              if (l.dropdown) {
                return (
                  <div key={l.label} className="flex flex-col gap-2">
                    <span className="text-sm font-semibold text-[#0A1F47]/60">{l.label}</span>
                    <div className="flex flex-col gap-2 pl-4">
                      {l.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setOpen(false)}
                          className="text-sm font-medium text-[#0A1F47]/80 hover:text-[#2E6BF6] transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return renderLink(l);
            })}
            <Link to="/#contact" onClick={() => setOpen(false)} className="btn-navy w-max">
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
